import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const STEPPER_COLUMN_WIDTH = 220;
const STEPPER_COLUMN_MAX_WIDTH = 240;
const VISUS_ORANGE = 'var(--rd-primary)';

function AlignedStepIcon({ index, active, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      tabIndex={-1}
      onClick={onClick}
      aria-current={active ? 'step' : undefined}
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.875rem',
        fontWeight: 600,
        cursor: 'pointer',
        border: 'none',
        p: 0,
        bgcolor: active ? 'var(--rd-accent)' : 'grey.300',
        color: active ? 'common.white' : 'text.secondary',
        flexShrink: 0,
        zIndex: 1,
        '&:hover': {
          bgcolor: active ? 'var(--rd-primary-hover)' : 'grey.400',
        },
      }}
    >
      {index + 1}
    </Box>
  );
}

export default function ReportStepper({
  steps,
  activeStep,
  onStepChange,
  renderStepContent,
  onStepFocus,
  sectionRefs,
  contentRef,
}) {
  const scrollRef = useRef(null);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateScrollHint = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      setCanScrollDown(false);
      return;
    }
    setCanScrollDown(el.scrollHeight - el.scrollTop - el.clientHeight > 12);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    updateScrollHint();
    el.addEventListener('scroll', updateScrollHint, { passive: true });
    const resizeObserver = new ResizeObserver(updateScrollHint);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener('scroll', updateScrollHint);
      resizeObserver.disconnect();
    };
  }, [steps, updateScrollHint]);

  useEffect(() => {
    if (contentRef) {
      contentRef.current = scrollRef.current;
    }
  }, [contentRef, steps]);

  if (!steps || steps.length === 0) {
    return (
      <Box ref={scrollRef} sx={{ flex: 1, overflow: 'auto', minHeight: 0 }} />
    );
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          pr: 0.5,
        }}
      >
        {steps.map((step, index) => {
          const isActive = activeStep === index;
          const isLast = index === steps.length - 1;

          return (
            <Box
              key={step.key || index}
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'stretch',
                mb: isLast ? 0 : 2,
              }}
            >
              <Box
                sx={{
                  width: STEPPER_COLUMN_WIDTH,
                  maxWidth: STEPPER_COLUMN_MAX_WIDTH,
                  flexShrink: 0,
                  display: 'flex',
                  gap: 1,
                  alignSelf: 'stretch',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: 28,
                    flexShrink: 0,
                    ml: 0.5,
                    pt: 2,
                  }}
                >
                  <AlignedStepIcon
                    index={index}
                    active={isActive}
                    onClick={() => onStepChange(index)}
                  />
                  {!isLast && (
                    <Box
                      sx={{
                        width: 2,
                        flex: 1,
                        bgcolor: 'grey.300',
                        mt: 0.5,
                        minHeight: 16,
                      }}
                    />
                  )}
                </Box>
                <Typography
                  component="button"
                  type="button"
                  tabIndex={-1}
                  onClick={() => onStepChange(index)}
                  sx={{
                    pt: 2,
                    pr: 1,
                    fontSize: '0.85rem',
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? 'var(--rd-accent)' : 'text.primary',
                    textAlign: 'left',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    lineHeight: 1.3,
                    alignSelf: 'flex-start',
                  }}
                >
                  {step.label}
                </Typography>
              </Box>

              <Box
                ref={(el) => {
                  if (sectionRefs) sectionRefs.current[index] = el;
                }}
                onFocusCapture={() => onStepFocus?.(index)}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  border: '1px solid',
                  borderColor: isActive ? 'var(--rd-accent)' : 'divider',
                  borderWidth: isActive ? 2 : 1,
                  borderRadius: 1,
                  p: 2,
                  scrollMarginTop: 8,
                }}
              >
                {renderStepContent(step, index)}
              </Box>
            </Box>
          );
        })}
      </Box>

      {canScrollDown && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: STEPPER_COLUMN_WIDTH,
            maxWidth: STEPPER_COLUMN_MAX_WIDTH,
            height: 40,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-start',
            pl: 2.25,
            pb: 0.5,
            pointerEvents: 'none',
            background: (theme) =>
              `linear-gradient(to bottom, transparent, ${theme.palette.background.paper} 70%)`,
          }}
        >
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 22,
              color: VISUS_ORANGE,
              animation: 'stepperBounce 1.6s ease-in-out infinite',
              '@keyframes stepperBounce': {
                '0%, 100%': { transform: 'translateY(0)' },
                '50%': { transform: 'translateY(3px)' },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
